// Public signing page for a job candidate's onboarding documents.
// Route: /hr-sign/:token (no auth). Renders every pending document under
// the token and signs all of them in one action ("adopt once, apply to all"
// — same UX as PacketSign.js, without its client/PDF-specific machinery).

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { renderSignedDocumentBlob, blobToBase64 } from '../lib/documents/signedDocumentPdf';
import { CRM_EMAIL } from '../lib/org';

const FN = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/`;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Which of the doc types signed through this page get a real PDF filed to
// Drive (offer_letter/job_description/background_check_consent/
// orientation_checklist) — training docs are handled by their own separate
// flow already (per-module certificates, not a single signed document).
const NOT_EMAILED = ['placement_acknowledgment'];
const PDF_DOC_TYPES = ['offer_letter', 'job_description', 'background_check_consent', 'orientation_checklist', 'agency_worker_acknowledgment', 'placement_acknowledgment', 'policy_acknowledgment', 'confidentiality_agreement', 'orientation_attestation'];

// Chrome text only — the document `content` itself was already generated in
// whichever language the office picked when preparing it (see
// onboardingTemplates.js), so this page just needs to match that language
// rather than offer its own independent toggle (switching only the chrome
// while the document body stayed fixed would be confusing, not helpful).
const TRANSLATIONS = {
  en: {
    linkUnavailable: 'Link Unavailable',
    onboardingDocuments: 'Onboarding Documents',
    instructions: 'Please read each document below, then type your full legal name once to sign all of them. Your typed name is a legally binding electronic signature (ESIGN Act / UETA).',
    countersignedBy: 'Countersigned by', onBehalfOf: 'on behalf of A Heart For Care',
    signedBy: 'Signed by',
    thankYou: '✓ Thank you — your signature has been applied. You are now active with A Heart For Care; check your email for your AxisCare login and next steps.',
    signAllAbove: 'Sign all documents above', typeFullName: 'Type your full legal name',
    applyingSignature: 'Applying signature…',
    adoptSignAll: (n) => `Adopt & Sign All (${n} document${n === 1 ? '' : 's'})`,
    typeNameError: 'Type your full legal name to sign.',
    signFailed: 'Could not complete signing:',
    questionsContact: 'Questions? Contact',
    loading: 'Loading…',
  },
  es: {
    linkUnavailable: 'Enlace No Disponible',
    onboardingDocuments: 'Documentos de Incorporación',
    instructions: 'Por favor lea cada documento a continuación, luego escriba su nombre legal completo una vez para firmarlos todos. Su nombre escrito es una firma electrónica legalmente vinculante (Ley ESIGN / UETA).',
    countersignedBy: 'Contrafirmado por', onBehalfOf: 'en representación de A Heart For Care',
    signedBy: 'Firmado por',
    thankYou: '✓ Gracias — su firma ha sido aplicada. Ya está activo(a) con A Heart For Care; revise su correo para su acceso a AxisCare y los próximos pasos.',
    signAllAbove: 'Firme todos los documentos arriba', typeFullName: 'Escriba su nombre legal completo',
    applyingSignature: 'Aplicando firma…',
    adoptSignAll: (n) => `Adoptar y Firmar Todo (${n} documento${n === 1 ? '' : 's'})`,
    typeNameError: 'Escriba su nombre legal completo para firmar.',
    signFailed: 'No se pudo completar la firma:',
    questionsContact: '¿Preguntas? Contacte a',
    loading: 'Cargando…',
  },
};

export default function HRDocSign() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [adoptName, setAdoptName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const t = TRANSLATIONS[state.preferredLanguage === 'es' ? 'es' : 'en'];

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function load() {
    try {
      const r = await fetch(`${FN}get-staff-doc-by-token?token=${token}`, {
        headers: { Authorization: `Bearer ${ANON}`, apikey: ANON },
      });
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'This link is invalid or expired. / Este enlace no es válido o ha expirado.'); setState({ loading: false }); return; }
      setState({ loading: false, ...j });
      if ((j.documents || []).every(d => d.status === 'signed')) setDone(true);
    } catch (e) { setErr(e.message); setState({ loading: false }); }
  }

  async function signAll() {
    const name = (adoptName || '').trim();
    if (!name) { setErr(t.typeNameError); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${FN}submit-staff-signatures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON },
        body: JSON.stringify({ token, signedName: name }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || 'Sign failed');
      setDone(true);
      fileSignedDocumentPdfs(name);
    } catch (e) {
      setErr(`${t.signFailed} ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  // Best-effort — files a real PDF copy of each newly-signed document to
  // Drive, then sends the candidate ONE email with all of them attached. Never
  // blocks the "thank you" screen already shown by setDone(true) above; a Drive
  // hiccup here shouldn't make the candidate think signing itself failed.
  //
  // Each document is still filed on its own — that is the record — but the
  // email used to be sent per document too, so a six-document bundle arrived
  // as six emails. Now it is one, sent once everything has rendered. Both
  // calls carry the signing link's token, which the function checks before it
  // will file or email anything.
  async function fileSignedDocumentPdfs(signedName) {
    const nowStr = new Date().toLocaleString();
    const lang = state.preferredLanguage === 'es' ? 'es' : 'en';
    const eligible = (state.documents || []).filter(d => PDF_DOC_TYPES.includes(d.doc_type));
    const post = (body) => fetch(`${FN}upload-signed-document`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON },
      body: JSON.stringify({ token, lang, ...body }),
    });

    const rendered = await Promise.all(eligible.map(async (doc) => {
      try {
        const blob = await renderSignedDocumentBlob({
          docLabel: doc.doc_label || doc.doc_type,
          staffName: signedName,
          contentHtml: doc.content,
          candidateSignedName: signedName,
          candidateSignedAt: nowStr,
          officeSignedBy: doc.office_signed_by,
          officeSignerRole: doc.office_signer_role,
          officeSignedAt: doc.office_signed_at ? new Date(doc.office_signed_at).toLocaleString() : null,
          recordId: doc.id,
        });
        const fileBase64 = await blobToBase64(blob);
        const item = {
          docId: doc.id,
          staffId: doc.staff_id,
          docType: doc.doc_type,
          docLabel: doc.doc_label || doc.doc_type,
          filename: `${(doc.doc_label || doc.doc_type).replace(/[^a-z0-9]+/gi, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`,
          fileBase64,
        };
        await post({ fileBase64, filename: item.filename, staffId: item.staffId, docId: item.docId });
        return item;
      } catch (pdfErr) {
        console.error('Signed-document PDF filing failed (non-blocking):', pdfErr);
        return null;
      }
    }));

    // One email for the lot. Everything under one link belongs to one person.
    // An agency nurse's Placement Acknowledgment is filed but not emailed:
    // A Heart For Care sends her nothing (agreement §5, §7) — her agency
    // downloads the signed copy from its portal.
    const docs = rendered.filter(Boolean).filter((d) => !NOT_EMAILED.includes(d.docType));
    if (!docs.length) return;
    try {
      await post({
        mode: 'email_bundle',
        staffId: docs[0].staffId,
        documents: docs.map(({ docId, docLabel, filename, fileBase64 }) => ({ docId, docLabel, filename, fileBase64 })),
      });
    } catch (mailErr) {
      console.error('Signed-documents email failed (non-blocking):', mailErr);
    }
  }

  if (state.loading) return <Shell><p>{t.loading}</p></Shell>;
  if (err && !state.documents) return <Shell><h2>{t.linkUnavailable}</h2><p style={{ color: 'var(--text3)' }}>{err}</p></Shell>;

  const { firstName, documents = [] } = state;

  return (
    <Shell>
      <h1 style={{ color: 'var(--text)', margin: '0 0 4px' }}>A Heart For Care</h1>
      <p style={{ color: 'var(--text3)', marginTop: 0 }}>{t.onboardingDocuments}{firstName ? ` — ${firstName}` : ''}</p>

      <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0 20px', paddingTop: 12 }}>
        <p style={{ color: 'var(--text3)', fontSize: 14, marginTop: 0 }}>
          {t.instructions}
        </p>
      </div>

      {documents.map(doc => (
        <div key={doc.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: doc.status === 'signed' ? 'var(--success)' : 'var(--border2)' }}>{doc.status === 'signed' ? '✓' : '○'}</span>
            {doc.doc_label || doc.doc_type}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, maxHeight: 260, overflowY: 'auto' }}
            dangerouslySetInnerHTML={{ __html: doc.content || '' }} />
          {doc.office_signed_by && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
              {t.countersignedBy} {doc.office_signed_by}{doc.office_signer_role ? ` (${doc.office_signer_role})` : ''} {t.onBehalfOf} — {new Date(doc.office_signed_at).toLocaleString()}
            </div>
          )}
          {doc.status === 'signed' && (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--success)' }}>{t.signedBy} {doc.signed_name} — {new Date(doc.signed_at).toLocaleString()}</div>
          )}
        </div>
      ))}

      {err && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 8, borderRadius: 6, marginBottom: 10 }}>{err}</div>}

      {done ? (
        <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: 14, borderRadius: 8, fontWeight: 600 }}>
          {t.thankYou}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--accent-border)', borderRadius: 10, padding: 18, background: 'var(--bg2)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)', marginBottom: 4 }}>{t.signAllAbove}</div>
          <input placeholder={t.typeFullName} value={adoptName}
            onChange={(e) => setAdoptName(e.target.value)}
            style={{ width: '100%', padding: 12, border: '1px solid var(--border2)', borderRadius: 6, fontSize: 16, boxSizing: 'border-box' }} />
          {adoptName.trim() && (
            <div style={{ color: 'var(--text)', fontFamily: 'cursive', fontSize: 26, margin: '10px 2px 4px' }}>{adoptName.trim()}</div>
          )}
          <button disabled={busy || !adoptName.trim()} onClick={signAll}
            style={{ marginTop: 14, width: '100%', background: adoptName.trim() ? 'var(--success)' : 'var(--border2)', color: '#fff', border: 'none', borderRadius: 8, padding: 14, cursor: adoptName.trim() ? 'pointer' : 'default', fontWeight: 700, fontSize: 15 }}>
            {busy ? t.applyingSignature : t.adoptSignAll(documents.length)}
          </button>
        </div>
      )}

      <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16 }}>
        {t.questionsContact} <a href={`mailto:${CRM_EMAIL}`}>{CRM_EMAIL}</a>.
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 640, width: '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }}>
        {children}
      </div>
    </div>
  );
}
